import React, { FormEvent, useContext, useState } from "react";
import { useMultiPages } from "./useMultiPages";
import { Button } from "@components/ui/button";
import { ArrowRight, ArrowLeft, BookmarkCheck, Loader2 } from "lucide-react";
import { GeneralInfo } from "./GeneralInfo";
import { UIPage } from "./UIPage";
import InitialPage from "./InitialPage";
import {
  FormData,
  INFO_DATA,
  UIPageType,
  pluginInfo,
  DropdownOption,
  LogicTypeWithState,
  BlockRoot,
  DropdownNode,
  LogicPagePayload,
  VariableItem,
} from "./allData";
import LogicPage from "./LogicPage";
import { toast } from "@components/ui/use-toast";
import { useMutation, useQuery } from "react-query";
import {
  fetchConfigPage,
  fetchLogicPageInfo,
  fetchPluginInfo,
  fetchUIPageData,
  saveFullPluginInfo,
} from "./createPluginApi";
import PageLoader from "@components/loaders/PageLoader";
import { GeneralContext } from "@services/providers/GeneralProvider";
import {
  MeshPluginProvider,
  useMeshPluginContext,
  MeshPluginActions,
} from "@services/providers/MeshPlugin";
import { useNavigate } from "react-router-dom";

const CreatePlugin: React.FC = () => {
  return (
    <MeshPluginProvider>
      <CreatePluginContent />
    </MeshPluginProvider>
  );
};

const CreatePluginContent: React.FC = () => {
  const { isAdminUser, productType } = useContext(GeneralContext);
  const {
    pluginId,
    uiData,
    isSavedSection,
    definedVariables,
    selectedLogicName,
    hasUnsavedGeneralInfo,

    dispatch,
  } = useMeshPluginContext();
  const [infoData, setInfoData] = useState<FormData & { tagInput?: string }>(
    INFO_DATA,
  );
  const [completedSections, setCompletedSections] = useState<{
    info: boolean;
    ui: boolean;
    logic: boolean;
  }>({
    info: false,
    ui: false,
    logic: false,
  });
  const [selectedApiData, setSelectedApiData] =
    useState<Partial<pluginInfo> | null>(null);
  // states for the logic page

  const [, setDropdownBlocks] = useState<BlockRoot[]>([]);

  const [alldropdownTrees, setAlldropdownTrees] = useState({
    dropdownTrees: [] as DropdownNode[][],
    configDropdownTrees: [] as DropdownNode[][],
  });

  // const [dropdownTrees, setDropdownTrees] = useState<DropdownNode[][]>([]);

  const [dropdownInput, setDropdownInput] = useState<DropdownOption>({
    label: "",
    value: "",
    target: "",
    resValues: [],
  });

  const [logicType, setLogicType] = useState<LogicTypeWithState[]>([
    {
      name: "Action",
      data: [],
    },
    {
      name: "Save Logs",
      data: [],
    },
    {
      name: "Settings",
      data: [],
    },
  ]);
  const navigate = useNavigate();

  const sessionEditPluginId = React.useMemo(
    () => sessionStorage.getItem("editPluginId"),
    [],
  );
  const sessionMeshPluginId = React.useMemo(
    () => sessionStorage.getItem("meshPluginId"),
    [],
  );
  const resolvedPluginId =
    pluginId || sessionEditPluginId || sessionMeshPluginId;

  React.useEffect(() => {
    if (productType !== "cmx" && !isAdminUser) {
      toast({
        variant: "destructive",
        title: "Access Denied",
        description: "You don't have permission to access this page.",
        duration: 3000,
      });
      window.location.href = "/";
    }
  }, [isAdminUser, productType]);

  // if the route is edit, then we need to fetch the plugin info
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isEdit = params.get("edit");
    const createPluginEntry = sessionStorage.getItem("createPluginEntry");
    const editPluginId = sessionStorage.getItem("editPluginId");
    const meshPluginIdFromSession = sessionStorage.getItem("meshPluginId");

    const sessionPluginId = editPluginId || meshPluginIdFromSession;

    // Explicit create button entry: allow initial page once.
    if (createPluginEntry === "1" && isEdit !== "1") {
      return;
    }

    // Edit route: recover plugin context or redirect to listing.
    if (isEdit === "1") {
      if (!sessionPluginId) {
        sessionStorage.removeItem("createPluginEntry");
        toast({
          variant: "destructive",
          title: "Plugin Not Found",
          description: "Create a new plugin or edit an existing one.",
          duration: 2500,
        });
        navigate("/ai-engine", { replace: true });
        return;
      }

      dispatch({
        type: MeshPluginActions.SET_PLUGIN_ID,
        payload: sessionPluginId,
      });
      sessionStorage.removeItem("createPluginEntry");
      goToPage(1);
      return;
    }

    // URL lost `edit=1` (e.g. re-login redirect): restore edit mode.
    if (sessionPluginId) {
      dispatch({
        type: MeshPluginActions.SET_PLUGIN_ID,
        payload: sessionPluginId,
      });
      sessionStorage.removeItem("createPluginEntry");
      navigate("?edit=1", { replace: true });
      goToPage(1);
      return;
    }

    // No explicit create-entry and no edit context: treat as expired session.
    sessionStorage.removeItem("createPluginEntry");
    navigate("/ai-engine", { replace: true });
  }, [dispatch, navigate]);

  // Check if we're in edit mode
  const isEditMode =
    new URLSearchParams(window.location.search).get("edit") === "1";

  // Add refs to track hydration for each data type
  const hasHydratedLogic = React.useRef(false);
  const hasHydratedInfo = React.useRef(false);
  const hasHydratedConfig = React.useRef(false);
  const hasHydratedUI = React.useRef(false);

  // First, fetch plugin info to get flags for conditional API calls
  const { data: pluginInfoData } = useQuery({
    queryKey: ["pluginInfo", resolvedPluginId],
    queryFn: () => fetchPluginInfo(resolvedPluginId!),
    enabled: !!resolvedPluginId && isEditMode && !hasHydratedInfo.current,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
    cacheTime: Infinity,
    retry: false,
  });

  const hasLogicPageLocal = pluginInfoData?.is_logic_page ?? false;
  const hasUIPageLocal = pluginInfoData?.is_ui_page ?? false;
  const hasTempConfigPageLocal = pluginInfoData?.temp_config_page ?? false;

  // Add this query in CreatePlugin.tsx (around line 150)
  const { data: fetchedLogicPageData } = useQuery(
    ["fetchLogicPageData", resolvedPluginId],
    () => fetchLogicPageInfo(resolvedPluginId ?? ""),
    {
      enabled: !!resolvedPluginId && isEditMode && hasLogicPageLocal,
    },
  );

  // Add effect to hydrate logic data ONCE
  React.useEffect(() => {
    if (
      !hasHydratedLogic.current &&
      fetchedLogicPageData?.status === 1 &&
      fetchedLogicPageData.data &&
      resolvedPluginId
    ) {
      hasHydratedLogic.current = true;

      // Create dispatch wrappers with access to current state
      const updateDefinedVariables = (value: any) => {
        const resolved =
          typeof value === "function" ? value(definedVariables) : value;
        dispatch({
          type: MeshPluginActions.SET_DEFINED_VARIABLES,
          payload: resolved,
        });
      };

      const updateSelectedLogicName = (value: any) => {
        const resolved =
          typeof value === "function" ? value(selectedLogicName) : value;
        dispatch({
          type: MeshPluginActions.SET_SELECTED_LOGIC_NAME,
          payload: resolved,
        });
      };

      const updateIsSavedSection = (value: any) => {
        const resolved =
          typeof value === "function" ? value(isSavedSection) : value;
        dispatch({
          type: MeshPluginActions.SET_IS_SAVED_SECTION,
          payload: resolved,
        });
      };

      parseLogicPayloadToState(
        fetchedLogicPageData.data,
        [
          { name: "Action", data: [] },
          { name: "Save Logs", data: [] },
          { name: "Settings", data: [] },
        ],
        setLogicType,
        updateDefinedVariables,
        updateSelectedLogicName,
        () => {}, // setSelectedLogicBlockId placeholder (local state in LogicPage)
        updateIsSavedSection,
        selectedLogicName,
      );
    }
  }, [
    fetchedLogicPageData?.status,
    fetchedLogicPageData?.data,
    dispatch,
    resolvedPluginId,
    definedVariables,
    selectedLogicName,
    isSavedSection,
  ]);

  function getBundleTypeName(type: string) {
    switch (type) {
      case "parquet":
        return "Save Logs";
      case "setting":
        return "Settings";
      case "action":
        return "Action";
      case "datalist":
        return "Data List";
      default:
        return type;
    }
  }

  function parseLogicPayloadToState(
    payload: LogicPagePayload,
    defaultLogicTypeList: VariableItem,
    setLogicType: React.Dispatch<React.SetStateAction<LogicTypeWithState[]>>,
    setDefinedVariables: React.Dispatch<React.SetStateAction<any>>,
    setSelectedLogicName: React.Dispatch<React.SetStateAction<string>>,
    setSelectedLogicBlockId: React.Dispatch<React.SetStateAction<number>>,
    updateIsSavedSection?: React.Dispatch<React.SetStateAction<any>>,
    preferredSelectedLogicName?: string,
  ) {
    const { LogicBundles = [], variables: variablesData = [] } = payload;

    // Group bundles by logic type name
    const logicTypeMap: Record<string, any[]> = {};
    LogicBundles.forEach((bundle: any) => {
      const logicName = getBundleTypeName(
        bundle.bundleType || bundle.bundleTitle || "Action",
      );
      if (!logicTypeMap[logicName]) logicTypeMap[logicName] = [];
      logicTypeMap[logicName].push(bundle);
    });

    // Build logicType state to match the structure used when saving
    const finalLogicTypes = defaultLogicTypeList.map((defaultLogic) => {
      const bundles = logicTypeMap[defaultLogic.name] || [];
      const blocks = bundles.map((bundle: any) => {
        // If mappings exist, use the first mapping for block meta-data
        const mapping =
          Array.isArray(bundle.mappings) && bundle.mappings.length > 0
            ? bundle.mappings[0]
            : null;

        const bundleVariables = variablesData.filter(
          (v: any) => v.sourceBundleId == bundle.bundleId,
        );

        return {
          id: bundle.bundleId,
          logUid: mapping?.logUid ?? "",
          title: bundle.bundleTitle || "Untitled",
          description: bundle.bundleDescription || "",
          bundleCronTime: bundle.bundleCronTime || "",
          bundleDependency: bundle.bundleDependency || null,
          bundleActivate: bundle.bundleActivate ?? true,
          category: mapping?.logCategory ?? "",
          ls_product_type: mapping?.log_ls_product_type ?? [],
          mappingFields: mapping?.mappingFields ?? [],
          requests: bundle.requests || [],
          blocks: (bundle.requests || []).map((req: any, index: number) => ({
            id: index,
            response: req.response || "",
          })),
          finalRequestData: {
            request: (bundle.requests || []).map((req: any) => ({
              ...req,
              body:
                req.body_type === "x-www-form-urlencoded" &&
                typeof req.body === "object"
                  ? ""
                  : req.body || "",
              formData:
                req.body_type === "x-www-form-urlencoded" &&
                typeof req.body === "object"
                  ? Object.entries(req.body || {}).map(([key, value]) => ({
                      key,
                      value: String(value),
                      enabled: true,
                    }))
                  : [],
              bodyType:
                req.body_type === "x-www-form-urlencoded"
                  ? "form-urlencoded"
                  : req.body_type || "none",
              rawBodyType: req.raw_type || "text",
            })),
            variables: bundleVariables,
            responses: {},
          },
          response: {},
          isResponse: mapping ? true : "",
        };
      });

      return {
        name: defaultLogic.name,
        description: blocks[0]?.description || "",
        data: blocks,
      };
    });

    // Parse variables
    const parsedVariables = variablesData.map((v: any) => ({
      id: v.id,
      name: v.name,
      isCommon: v.scope === "global",
      scope: v.scope || "local",
      sourceBundleId: v.sourceBundleId ?? 0,
      sourceRequestId: v.sourceRequestId ?? 0,
      sourcePath: v.sourcePath,
      type: v.type || "string",
      variableType: v.variableType || "",
      value: v.value || "",
      isAuthentication: v.isAuthentication || false,
    }));

    setLogicType(finalLogicTypes);
    setDefinedVariables(parsedVariables);

    if (finalLogicTypes?.length > 0) {
      const preferredLogic = finalLogicTypes.find(
        (logic) => logic.name === preferredSelectedLogicName,
      );
      const selectedLogic = preferredLogic ?? finalLogicTypes[0];

      setSelectedLogicName(selectedLogic.name);
      if (selectedLogic.data.length > 0) {
        setSelectedLogicBlockId(selectedLogic.data[0].id);
      }
    }

    updateIsSavedSection?.((prev: { name: string; isSaved: boolean }[]) =>
      prev.map((section) =>
        section.name === "logic" ? { ...section, isSaved: true } : section,
      ),
    );
  }

  // Function to update fields in the form data
  function updateFields(fields: Partial<FormData>) {
    setInfoData((prev) => {
      return { ...prev, ...fields };
    });
  }

  // get plugin info data using useQuery
  // when we have the pluginInfoData, we need to set the fields
  React.useEffect(() => {
    if (!pluginInfoData) return;

    hasHydratedInfo.current = true;

    const data = pluginInfoData.data || pluginInfoData;

    if (!data) {
      toast({
        variant: "destructive",
        title: "Error Fetching Plugin Info",
        description: "No data found for the given plugin ID.",
        duration: 3000,
      });
      return;
    }

    dispatch({
      type: MeshPluginActions.SET_HAS_LOGIC_PAGE,
      payload: pluginInfoData.is_logic_page ?? false,
    });
    dispatch({
      type: MeshPluginActions.SET_HAS_UI_PAGE,
      payload: pluginInfoData.is_ui_page ?? false,
    });
    dispatch({
      type: MeshPluginActions.SET_HAS_TEMP_CONFIG_PAGE,
      payload: pluginInfoData.temp_config_page ?? false,
    });

    updateFields({
      pluginName: data.pluginName || "",
      version: data.version || "",
      tags: data.tags || [],
      description1: data.description1 || "",
      description2: data.description2 || "",
      ls_version: data.ls_version || { minimum: "4.7.2" },
      author: data.author || "",
      productType: data.productType || "",
      ls_product_type: data.ls_product_type || [],
      pluginDependency: data.pluginDependency || { name: "", id: "" },
      pluginLogo: data.pluginLogo || "",
      type: data.type || "",
      id: data.id || "",
      createdDate: data.createdDate || "",
      updatedDate: data.updatedDate || "",
    });

    // set info isSavedSection truthy
    dispatch({
      type: MeshPluginActions.SET_IS_SAVED_SECTION,
      payload: (prev: { name: string; isSaved: boolean }[]) =>
        prev.map((section: { name: string; isSaved: boolean }) =>
          section.name === "info" ? { ...section, isSaved: true } : section,
        ),
    });
    setCompletedSections((prev) => ({ ...prev, info: true }));
  }, [pluginInfoData]);

  const isAlreadySavedPluginInfo = isSavedSection.find(
    (section) => section.name === "info",
  )?.isSaved;

  // Mutation for saving plugin info
  const { mutate: savePluginInfo, isLoading: isSaving } = useMutation({
    mutationFn: saveFullPluginInfo,
    onSuccess: (_, variables) => {
      toast({
        variant: "success",
        title: `General Information ${isAlreadySavedPluginInfo ? "Updated" : "Saved"}`,
        description: `General plugin information has been ${isAlreadySavedPluginInfo ? "updated" : "saved"} successfully.`,
        duration: 3000,
      });

      // Store plugin ID in sessionStorage and switch to edit mode
      const savedPluginId = variables.id;
      sessionStorage.setItem("editPluginId", savedPluginId);

      // Navigate to edit mode if not already in edit mode
      const params = new URLSearchParams(window.location.search);
      if (params.get("edit") !== "1") {
        navigate("?edit=1", { replace: true });
      }

      // Clear unsaved changes flag
      dispatch({
        type: MeshPluginActions.SET_HAS_UNSAVED_GENERAL_INFO,
        payload: false,
      });

      // Mark info section as saved so subsequent saves are treated as updates
      dispatch({
        type: MeshPluginActions.SET_IS_SAVED_SECTION,
        payload: (prev: { name: string; isSaved: boolean }[]) =>
          prev.map((section: { name: string; isSaved: boolean }) =>
            section.name === "info" ? { ...section, isSaved: true } : section,
          ),
      });

      // Only navigate if success
      nextPage();

      // Mark section complete
      setCompletedSections((prev) => ({ ...prev, info: true }));
    },
    onError: (err: any) => {
      let errorMessage = "Could not connect.";
      // Case 1: Server returned a structured error object
      if (err?.response?.data) {
        try {
          const data = err.response.data;

          // Some APIs return the message as a stringified JSON
          if (typeof data.message === "string" && data.message.includes("{")) {
            const inner = JSON.parse(data.message.split(" - ")[1] || "{}");
            errorMessage =
              inner.error_description || inner.error || data.message;
          } else {
            errorMessage =
              data.error_description || data.message || errorMessage;
          }
        } catch (parseError) {
          console.warn("Failed to parse error message:", parseError);
        }
      }

      // Case 2: Fallback to plain err.message if available
      else if (err?.message) {
        errorMessage = err.message;
      }

      toast({
        variant: "destructive",
        title: "Error Saving Plugin Info",
        description:
          errorMessage ||
          "Something went wrong while saving plugin information.",
        duration: 3000,
      });
    },
  });

  // Fetch config page data
  const { data: configPageData } = useQuery(
    ["configPage", resolvedPluginId, "temp"],
    () => fetchConfigPage(resolvedPluginId ?? "", "temp"),
    {
      enabled: !!resolvedPluginId && isEditMode && hasTempConfigPageLocal,
    },
  );

  // to show the column name as value if its isDecript or similar
  const filterConfigKeys = (config: Record<string, any>) => {
    const excludeKeys = ["isDecript", "testConnectionBtn"];
    return Object.fromEntries(
      Object.entries(config).filter(([key]) => !excludeKeys.includes(key)),
    );
  };

  // Update savedSettings and configurationData when configPageData changes
  React.useEffect(() => {
    if (!configPageData?.data) return;

    hasHydratedConfig.current = true;

    const newSavedSettings: Record<string, Record<string, any>[]> = {};
    const newConfigData: any[] = [];

    Object.entries(configPageData.data).forEach(([pageUid, configs]) => {
      const filteredConfigs = (configs as any[]).map(filterConfigKeys);
      newSavedSettings[pageUid] = filteredConfigs;
      newConfigData.push({ pageUid, data: filteredConfigs });
    });

    dispatch({
      type: MeshPluginActions.SET_SAVED_SETTINGS,
      payload: newSavedSettings,
    });
    dispatch({
      type: MeshPluginActions.SET_CONFIGURATION_DATA,
      payload: newConfigData,
    });
  }, [configPageData, dispatch]);

  // Fetch existing UI Page data when pluginId changes
  const { data: fetchedUIPageData } = useQuery(
    ["ui-page-data", resolvedPluginId],
    () => fetchUIPageData(resolvedPluginId!),
    {
      enabled: !!resolvedPluginId && isEditMode && hasUIPageLocal,
    },
  );
  // Update uiPages, fields, helpDocs when fetchedUIPageData changes
  React.useEffect(() => {
    if (fetchedUIPageData?.data) {
      hasHydratedUI.current = true;

      const pages = fetchedUIPageData.data;
      dispatch({ type: MeshPluginActions.SET_UI_PAGES, payload: pages });
      dispatch({ type: MeshPluginActions.SET_UI_DATA, payload: pages });

      const extractedFields: any[] = [];

      pages
        .filter((page: any) => page.pageType === "action")
        .forEach((page: any) => {
          (page.forms || []).forEach((form: any) => {
            (form.fieldList || []).forEach((field: any) => {
              const normalizedField = {
                fieldName: field.fieldTitle || "",
                fieldType: field.fieldType || "",
                defaultValue: field.default || "",
                dropdownOptions: (field.values || []).map((val: any) => ({
                  label: val.displayName || "",
                  value: val.value || "",
                })),
                dynamicRequest: field.dynamicRequest || "",
                isApiValue:
                  field.values?.some((val: any) => val.isDynamic === 1) ||
                  false,
                fromAnomaly: !!field.isAnomaly,
                userAction: field.userAction
                  ? {
                      trigger: field.userAction.trigger || "",
                      bundle: field.userAction.bundle || "",
                      bundleVariable: field.userAction.bundleVariable || "",
                      mapResponse: {
                        label: field.userAction.mapResponse?.label || "",
                        value: field.userAction.mapResponse?.value || "",
                      },
                      targetField: field.userAction.targetField || "",
                    }
                  : undefined,
              };

              extractedFields.push(normalizedField);
            });
          });
        });

      dispatch({
        type: MeshPluginActions.SET_ACTION_FIELDS,
        payload: extractedFields,
      });

      // Setup helpDocs only from help pages
      const helpDocsData = pages
        .filter((p: any) => p.pageType === "Help")
        .map((page: any) => ({
          title: page.pageTitle,
          content: page.helpContent || page.content || "",
        }));

      dispatch({
        type: MeshPluginActions.SET_HELP_DOCS,
        payload: helpDocsData,
      });

      dispatch({
        type: MeshPluginActions.SET_IS_SAVED_SECTION,
        payload: (prev: { name: string; isSaved: boolean }[]) =>
          prev.map((section: { name: string; isSaved: boolean }) =>
            section.name === "ui" ? { ...section, isSaved: true } : section,
          ),
      });
    }
  }, [fetchedUIPageData?.data]);

  // Using the custom hook to manage multi-page form state
  const {
    currentStepIndex,
    step,
    isFirstStep,
    isLastStep,
    initialPage,
    prevPage,
    nextPage,
    handleGoToPageIndex,
  } = useMultiPages([
    <InitialPage key="initial" />,
    <GeneralInfo
      key="info"
      {...infoData}
      updateFields={updateFields}
      goToPage={goToPage}
      isNewPlugin={!pluginId}
    />,
    <UIPage
      key="ui"
      {...uiData}
      pluginName={infoData.pluginName}
      goToPage={goToPage}
      updateUIPage={updateUIPage}
      dropdownInput={dropdownInput}
      setDropdownInput={setDropdownInput}
      logicType={logicType}
      dropdownBlocks={
        alldropdownTrees?.dropdownTrees.length > 0
          ? alldropdownTrees?.dropdownTrees[0]
          : []
      }
      setDropdownBlocks={setDropdownBlocks}
      alldropdownTrees={alldropdownTrees}
      setAlldropdownTrees={setAlldropdownTrees}
    />,
    <LogicPage
      key="logic"
      goToPage={goToPage}
      setSelectedApiData={setSelectedApiData}
      selectedApiData={selectedApiData}
      logicType={logicType}
      setLogicType={setLogicType}
    />,
  ]);

  React.useEffect(() => {
    const isProtectedEditStep = isEditMode && currentStepIndex >= 1;
    const isProtectedCreateStep = !isEditMode && currentStepIndex >= 2;

    if ((isProtectedEditStep || isProtectedCreateStep) && !resolvedPluginId) {
      toast({
        variant: "destructive",
        title: "Session Expired",
        description: "Plugin session was not found. Redirecting to AI Engine.",
        duration: 2500,
      });
      navigate("/ai-engine", { replace: true });
    }
  }, [currentStepIndex, isEditMode, navigate, resolvedPluginId]);

  // Function to validate the current step before proceeding
  function getValidationError(): string | null {
    const {
      pluginName,
      version,
      tags,
      description1,
      description2,
      ls_version,
      type,
      author,
      productType,
      ls_product_type,
      tagInput,
    } = infoData;

    // Check in order of appearance in the form
    if (!pluginName || !pluginName.trim()) {
      return "Plugin Name is required. Please enter a name for your plugin.";
    }

    // Require version to be non-empty and not just whitespace
    if (!version || !version.trim()) {
      return "Plugin Version is required. Please specify a version (e.g., 1.0 or 1.0.0).";
    }

    // Require version to be at least '1.0' or '1.0.0' format with only numbers
    const versionPattern = /^\d+\.\d+(\.\d+)?$/;
    if (!versionPattern.test(version.trim())) {
      return "Plugin Version must contain only numbers in the format '1.0' or '1.0.0' (e.g., 1.0, 2.5, 1.0.0, 2.5.3). No characters are allowed.";
    }

    // Special handling for tags
    if (tagInput && tagInput.trim()) {
      return "The value in the Tags field has not been added. Press Enter to confirm the tag before submitting.";
    }

    // Ensure at least one tag is present
    if (!tags || tags.length === 0) {
      return "At least one tag is required. Please add relevant tags to categorize your plugin and press Enter to confirm.";
    }

    // Validate each tag for non-empty and no spaces
    if (!description1 || !description1.trim()) {
      return "Plugin Description is required. Please provide a brief overview of what the plugin does for users (minimum 15 characters).";
    }

    if (description1.trim().length < 15) {
      return "Plugin Description is too short. Please provide at least 15 characters for clarity and completeness.";
    }

    if (description1.trim().length > 250) {
      return "Description 1 is too long. Please keep it under 250 characters to maintain consistency and readability.";
    }

    if (!description2 || !description2.trim()) {
      return "Developer Description is required. Please provide a brief overview of what the plugin does for creators (minimum 15 characters).";
    }

    if (description2.trim().length < 15) {
      return "Developer Description is too short. Please provide at least 15 characters for clarity and completeness.";
    }

    if (description2.trim().length > 250) {
      return "Developer Description is too long. Please keep it under 250 characters to maintain consistency and readability.";
    }

    if (!author || !author.trim()) {
      return "Author is required. Please specify the author or creator of this plugin.";
    }

    if (!ls_version || !ls_version.minimum || !ls_version.minimum.trim()) {
      return "LS Version is required. Please select the minimum LinkShadow version required for this plugin.";
    }

    // Only validate Type for admin users (non-admins get it set automatically)
    if (isAdminUser && (!type || !type.trim())) {
      return "Type is required. Please select whether this is a custom or generic plugin.";
    }

    if (!productType || !productType.trim()) {
      return "Product Type is required. Please select the type of product this plugin is associated with.";
    }

    // Only validate LS Product Type for admin users
    if (isAdminUser && (!ls_product_type || ls_product_type.length === 0)) {
      return "LS Product Type is required. Please select at least one LinkShadow product type that this plugin is compatible with.";
    }

    return null;
  }

  // Info Page Submission
  function handleInfoSubmit() {
    // Auto-add any unsaved tag input before submission
    if (infoData.tagInput && infoData.tagInput.trim()) {
      const trimmedTag = infoData.tagInput.trim();
      if (!infoData.tags.includes(trimmedTag)) {
        setInfoData((prev) => ({
          ...prev,
          tags: [...prev.tags, trimmedTag],
          tagInput: "", // Clear the input
        }));
      } else {
        // Clear duplicate tag input
        setInfoData((prev) => ({
          ...prev,
          tagInput: "",
        }));
      }
    }

    const alreadySaved = isSavedSection.find(
      (section) => section.name === "info",
    )?.isSaved;

    const now = Date.now();

    const updatedFields: Partial<FormData> = {
      updatedDate: now.toString(),
    };

    if (!infoData.id) {
      updatedFields.id = !alreadySaved
        ? `${infoData.pluginName.toLowerCase().replace(/\s+/g, "")}_generic_${now}`
        : pluginId;
    }

    if ((updatedFields.id || infoData.id || "") !== pluginId) {
      dispatch({
        type: MeshPluginActions.SET_PLUGIN_ID,
        payload: updatedFields.id || infoData.id || "",
      });
    }

    if (!infoData.createdDate) {
      updatedFields.createdDate = alreadySaved
        ? infoData.createdDate
        : now.toString();
    }

    const finalData: any = {
      ...infoData,
      ...updatedFields,
      is_edit: alreadySaved ? true : false,
      pluginDependency: infoData.pluginDependency.flat(),
      ls_product_type: infoData.ls_product_type.flat(),
      pluginLogo: infoData.pluginLogo ?? "",
    };
    savePluginInfo(finalData);
  }

  // ui secion submission
  function handleUISubmit() {
    // const now = Date.now().toString();

    // if (uiData.length === 0) {
    //   toast({
    //     variant: "destructive",
    //     title: "Missing UI Pages",
    //     description: "Please add at least one UI page (Help, Settings, Action).",
    //     duration: 3000,
    //   });
    //   return;
    // }
    setCompletedSections((prev) => ({ ...prev, ui: true }));
    nextPage();
    // toast({
    //   variant: "success",
    //   title: "Success",
    //   description: `Saved UI pages.`,
    //   duration: 3000,
    // });
  }

  // logic section submission
  const [isFinishing, setIsFinishing] = useState(false);

  async function finishPluginCreation() {
    const toastDurationMs = 1500;
    const now = Date.now().toString();

    setIsFinishing(true);

    setInfoData((prev) => ({
      ...prev,
      updatedDate: now,
    }));

    setCompletedSections((prev) => ({ ...prev, logic: true }));

    toast({
      variant: "success",
      title: "Saved",
      description: "Plugin has been saved successfully.",
      duration: toastDurationMs,
    });

    setTimeout(() => {
      navigate("/ai-engine");
    }, toastDurationMs);
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();

    // User has started progressing through create flow; consume entry token.
    if (currentStepIndex === 0) {
      sessionStorage.removeItem("createPluginEntry");
    }

    if (currentStepIndex === 1) {
      const validationError = getValidationError();

      if (validationError) {
        toast({
          variant: "destructive",
          title: "Validation Error",
          description: validationError,
          duration: 5000,
        });
        return;
      }
    }

    // Always trigger finish when Finish button is clicked (isLastStep)
    if (isLastStep) {
      finishPluginCreation();
      return;
    }

    switch (currentStepIndex) {
      case 1:
        if (hasUnsavedGeneralInfo) {
          handleInfoSubmit();
        } else {
          nextPage();
        }
        break;

      case 2:
        handleUISubmit();
        break;

      default:
        nextPage();
        break;
    }
  }

  // Function to update or add a UI page
  function updateUIPage(newPage: UIPageType) {
    dispatch({
      type: MeshPluginActions.SET_UI_DATA,
      payload: (prev: UIPageType[]) => {
        const filtered = prev.filter(
          (p: UIPageType) => p.pageType !== newPage.pageType,
        );
        return [...filtered, newPage];
      },
    });
  }

  // // Function to handle discard and continue action
  // function onDiscardAndContinue() {
  //   setHasUnsavedHelpDocs(false);
  //   setUnsavedConfirmationOpen(false);

  //   if (pendingPageIndex !== null) {
  //     goToPage(pendingPageIndex);
  //     setPendingPageIndex(null);
  //   }
  // }

  // // on close confirmation handler
  // const onCloseUnsavedConfirmation = () => {
  //   setUnsavedConfirmationOpen(false);
  // }

  // Function to navigate between pages with validation
  function goToPage(index: number) {
    // if (hasUnsavedHelpDocs) {
    //   setPendingPageIndex(index);
    //   setUnsavedConfirmationOpen(true);
    //   return;
    // }

    if (index === 1) {
      handleGoToPageIndex(1);
    } else if (index === 2) {
      if (completedSections.info) {
        handleGoToPageIndex(2);
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Please complete the General Information .",
          duration: 3000,
        });
      }
    } else if (index === 3) {
      if (completedSections.info) {
        handleGoToPageIndex(3);
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Please complete the General Information .",
          duration: 3000,
        });
      }
    }
  }

  return (
    <div className="m-10 p-5 border-secondary border-2 rounded-md w-[80%] h-[85vh]">
      <PageLoader show={isSaving} />
      {/* <Confirmation
        text={"You have unsaved Help Documents. Do you want to discard them and continue?"}
        confirmAction={onDiscardAndContinue}
        rejectAction={onCloseUnsavedConfirmation}
        confirmText="Discard and Continue"
        rejectText="Keep Editing"
        show={unsavedConfirmationOpen}
        closeDialog={onCloseUnsavedConfirmation}
      /> */}
      <div className="h-[96%]">{step}</div>
      <form onSubmit={onSubmit} className="ml-[250px]">
        <div className="flex justify-between">
          {!isFirstStep && !initialPage ? (
            <Button
              type="button"
              onClick={() => {
                // If in edit mode and on General Info page, go back to ai-engine
                if (currentStepIndex === 1) {
                  navigate("/ai-engine");
                } else {
                  prevPage();
                }
              }}
              className="bg-secondary text-foreground"
            >
              <ArrowLeft size={16} className="mr-1" />
              {currentStepIndex === 1 ? "Back to AI Engine" : "Back"}
            </Button>
          ) : (
            <span />
          )}
          <Button
            type="submit"
            disabled={isSaving || isFinishing}
            className={`flex text-foreground items-center border border-primary bg-transparent hover:bg-primary hover:text-background transition-colors px-5  ${isLastStep && "border-primaryGreen bg-transparent hover:bg-primaryGreen"}`}
          >
            {isLastStep ? (
              <div className="flex items-center">
                {isFinishing ? (
                  <>
                    Finishing...
                    <Loader2 size={18} className="ml-2 animate-spin" />
                  </>
                ) : (
                  <>
                    Finish <BookmarkCheck size={18} className="ml-2" />
                  </>
                )}
              </div>
            ) : initialPage ? (
              <>
                {" "}
                Start <ArrowRight size={16} className="ml-1" />
              </>
            ) : (
              <>
                {" "}
                {currentStepIndex == 1
                  ? hasUnsavedGeneralInfo
                    ? `${isAlreadySavedPluginInfo ? "Update" : "Save"} Info and Continue`
                    : "Next"
                  : "Next"}{" "}
                <ArrowRight size={16} className="ml-1" />
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreatePlugin;
